import type { InsurancePolicy, PolicyStatus, PolicyWithStatus, InsuranceMetrics, UpcomingPayment, ScheduledPremium } from '@/types/insurance';

/**
 * Calculate policy status based on current date vs start_date and end_date
 * Returns status ('active', 'expired', 'renewal_due') and daysUntilExpiration
 */
export function calculatePolicyStatus(policy: InsurancePolicy): {
  status: PolicyStatus;
  daysUntilExpiration: number;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day
  
  const startDate = new Date(policy.start_date);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(policy.end_date);
  endDate.setHours(0, 0, 0, 0);
  
  const daysUntilExpiration = Math.ceil(
    (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  let status: PolicyStatus;
  if (today > endDate) {
    status = 'expired';
  } else if (daysUntilExpiration <= 30) {
    status = 'renewal_due';
  } else {
    status = 'active';
  }
  
  return { status, daysUntilExpiration };
}

/**
 * Helper function to convert premium amount to monthly based on frequency
 */
function convertToMonthly(amount: number, frequency: string): number {
  switch (frequency) {
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'semi_annual':
      return amount / 6;
    case 'annual':
      return amount / 12;
    default:
      return amount;
  }
}

/**
 * Calculate insurance metrics including total coverage and premium costs
 * Converts all amounts to base currency using exchange rates
 * Groups metrics by policy type
 */
export function calculateInsuranceMetrics(
  policies: PolicyWithStatus[],
  baseCurrency: string,
  exchangeRates: Record<string, number>
): InsuranceMetrics {
  const activePolicies = policies.filter(
    p => p.status === 'active' || p.status === 'renewal_due'
  );
  
  const metrics: InsuranceMetrics = {
    totalCoverage: 0,
    monthlyPremiumCost: 0,
    annualPremiumCost: 0,
    activePolicyCount: activePolicies.length,
    metricsByType: {},
  };
  
  activePolicies.forEach(policy => {
    // Get exchange rate (default to 1 if same currency or rate not found)
    const rate = policy.currency === baseCurrency 
      ? 1 
      : (exchangeRates[policy.currency] || 1);
    
    const coverageInBase = policy.coverage_amount * rate;
    const premiumInBase = policy.premium_amount * rate;
    
    // Convert premium to monthly
    const monthlyPremium = convertToMonthly(premiumInBase, policy.premium_frequency);
    
    metrics.totalCoverage += coverageInBase;
    metrics.monthlyPremiumCost += monthlyPremium;
    metrics.annualPremiumCost += monthlyPremium * 12;
    
    // Group by type
    if (!metrics.metricsByType[policy.policy_type]) {
      metrics.metricsByType[policy.policy_type] = {
        coverage: 0,
        monthlyCost: 0,
        policyCount: 0,
      };
    }
    
    metrics.metricsByType[policy.policy_type].coverage += coverageInBase;
    metrics.metricsByType[policy.policy_type].monthlyCost += monthlyPremium;
    metrics.metricsByType[policy.policy_type].policyCount += 1;
  });
  
  return metrics;
}

/**
 * Calculate next payment date based on premium_frequency and last payment date
 * Uses start_date if no last payment exists
 * Handles month-end dates properly (e.g., Jan 31 + 1 month = Feb 28/29)
 */
export function calculateNextPaymentDate(
  policy: InsurancePolicy,
  lastPaymentDate?: string
): Date {
  const baseDate = lastPaymentDate 
    ? new Date(lastPaymentDate) 
    : new Date(policy.start_date);
  
  const nextDate = new Date(baseDate);
  const originalDay = nextDate.getDate();
  
  switch (policy.premium_frequency) {
    case 'monthly':
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
    case 'quarterly':
      nextDate.setMonth(nextDate.getMonth() + 3);
      break;
    case 'semi_annual':
      nextDate.setMonth(nextDate.getMonth() + 6);
      break;
    case 'annual':
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
  }
  
  // Handle month-end overflow: if the day changed due to overflow,
  // set it to the last day of the target month
  if (nextDate.getDate() !== originalDay) {
    nextDate.setDate(0); // Go to last day of previous month
  }
  
  return nextDate;
}

/**
 * Get upcoming payments due within 7 days
 * Calculates daysUntilDue for each upcoming payment
 * Sorts by daysUntilDue ascending
 */
export function getUpcomingPayments(
  policies: PolicyWithStatus[],
  policyPayments: Record<string, any[]>
): UpcomingPayment[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  
  const upcoming: UpcomingPayment[] = [];
  
  policies
    .filter(p => p.status === 'active' || p.status === 'renewal_due')
    .forEach(policy => {
      const payments = policyPayments[policy.id] || [];
      const lastPayment = payments[0]; // Assuming sorted by date desc
      
      const nextDate = calculateNextPaymentDate(policy, lastPayment?.transaction_date);
      
      if (nextDate <= sevenDaysFromNow && nextDate >= today) {
        const daysUntilDue = Math.ceil(
          (nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        
        upcoming.push({
          policyId: policy.id,
          policyType: policy.policy_type,
          providerName: policy.provider_name,
          amount: policy.premium_amount,
          currency: policy.currency,
          dueDate: nextDate.toISOString().split('T')[0],
          daysUntilDue,
        });
      }
    });
  
  return upcoming.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
}

/**
 * Generate a full premium schedule for a policy term.
 * Advances from startDate by the frequency interval, collecting due dates
 * until the date exceeds endDate.
 * Handles month-end overflow the same way calculateNextPaymentDate does.
 *
 * @returns Array of { dueDate, amount } objects — status is NOT computed here.
 */
export function generatePremiumSchedule(
  startDate: string,
  endDate: string,
  premiumFrequency: 'monthly' | 'quarterly' | 'semi_annual' | 'annual',
  premiumAmount: number
): Omit<ScheduledPremium, 'status'>[] {
  const monthsToAdvance: Record<typeof premiumFrequency, number> = {
    monthly: 1,
    quarterly: 3,
    semi_annual: 6,
    annual: 12,
  };

  const months = monthsToAdvance[premiumFrequency];
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  const schedule: Omit<ScheduledPremium, 'status'>[] = [];
  let current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  while (current <= end) {
    schedule.push({
      dueDate: current.toISOString().split('T')[0],
      amount: premiumAmount,
    });

    // Advance by the frequency interval, preserving the original day
    const originalDay = current.getDate();
    current = new Date(current);
    current.setMonth(current.getMonth() + months);

    // Handle month-end overflow: if the day changed, set to last day of target month
    if (current.getDate() !== originalDay) {
      current.setDate(0); // Last day of the previous month (which is the target month)
    }
  }

  return schedule;
}
